import { useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal, RolePicker } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { Role, TrainingItem, TrainingRole } from '../../lib/types';

/**
 * The training matrix — what each role has to know.
 *
 * A role gets here one of two ways: mirrored automatically from a People
 * Template, or added by hand for training that is not tied to a review
 * template. A row does not carry a name of its own: it POINTS at a job title
 * in core.Role, and the title shown is that role's. Retitling the job is done
 * on the Employees page and shows up here on the next load — what this screen
 * changes is WHICH role a training list belongs to.
 */
export function TrainingMatrix({ onChanged }: { onChanged?: () => void }) {
  const roles = useResource<TrainingRole[]>('/people/training/roles');
  const jobRoles = useResource<Role[]>('/roles');
  const [roleId, setRoleId] = useState<number | null>(null);
  const [addingRole, setAddingRole] = useState(false);
  const [renaming, setRenaming] = useState<TrainingRole | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const reload = () => {
    roles.reload();
    onChanged?.();
  };

  /* Deleting from the list rather than only from inside the role. The API
     takes the training items and everybody's completions with it in one
     transaction — see deleteRoleCascade — so there is nothing left half
     removed if it fails. */
  const remove = async (role: TrainingRole) => {
    setProblem(null);
    try {
      await api.del(`/people/training/roles/${role.TrainingRoleId}`);
      reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not delete that training list');
    }
  };

  if (roles.loading) return <Loading label="Loading roles" />;
  if (roles.error) return <ErrorBanner message={roles.error} />;

  const list = roles.data ?? [];
  /* Held by id rather than by object, so a rename elsewhere cannot leave a
     stale copy of the role on screen. */
  const role = list.find((r) => r.TrainingRoleId === roleId) ?? null;

  if (role) {
    return (
      <RoleTraining
        role={role}
        jobRoles={jobRoles.data ?? []}
        onBack={() => setRoleId(null)}
        onChanged={reload}
        onRename={() => setRenaming(role)}
        renaming={renaming}
        onCloseRename={() => setRenaming(null)}
      />
    );
  }

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="muted small" style={{ maxWidth: '60ch' }}>
          Required and optional training per role. Roles mirrored from a People Template appear here
          on their own; add one by hand for training that is not tied to a review template.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setAddingRole(true)}>
          Add role
        </button>
      </div>

      {problem && <ErrorBanner message={problem} />}

      {list.length === 0 ? (
        <Empty title="No roles yet">
          <p className="muted">
            Add one here, or create a People Template — its role appears on this matrix
            automatically.
          </p>
        </Empty>
      ) : (
        <div className="table-wrap card">
          <table className="table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Source</th>
                <th className="num">Required</th>
                <th className="num">Optional</th>
                <th className="actions" />
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.TrainingRoleId}>
                  <td className="strong">
                    {r.Name ?? <span className="subtle">No role assigned</span>}
                  </td>
                  <td>
                    {r.TemplateId ? (
                      <span className="badge badge-neutral">People Template</span>
                    ) : (
                      <span className="xs subtle">Added here</span>
                    )}
                  </td>
                  <td className="num">{r.RequiredCount}</td>
                  <td className="num">{r.ItemCount - r.RequiredCount}</td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setRoleId(r.TrainingRoleId)}
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setRenaming(r)}
                    >
                      Change role
                    </button>
                    {/* A list that mirrors a People Template is not deletable
                        here and the API refuses it too: it exists because the
                        template does, so deleting the template is the honest
                        way to remove it. Saying so beats an absent button
                        somebody reads as a bug. */}
                    {r.TemplateId === null ? (
                      <DeleteButton
                        what={`the "${r.Name ?? 'unassigned'}" training list and its ${r.ItemCount} ${
                          r.ItemCount === 1 ? 'item' : 'items'
                        }`}
                        onConfirm={() => void remove(r)}
                      />
                    ) : (
                      <span className="xs subtle" title="Delete its People Template instead.">
                        From a template
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addingRole && (
        <RolePickerDialog
          title="Add a training list"
          jobRoles={jobRoles.data ?? []}
          onClose={() => setAddingRole(false)}
          onSave={async (roleId) => {
            await api.post('/people/training/roles', { roleId });
            setAddingRole(false);
            reload();
          }}
        />
      )}

      {renaming && (
        <RolePickerDialog
          title="Change role"
          jobRoles={jobRoles.data ?? []}
          initial={renaming.RoleId}
          linked={renaming.TemplateId !== null}
          onClose={() => setRenaming(null)}
          onSave={async (roleId) => {
            await api.put(`/people/training/roles/${renaming.TrainingRoleId}`, { roleId });
            setRenaming(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/**
 * Which job role a training list belongs to.
 *
 * This was a text box, and what it did was not what it looked like: the title
 * typed here was resolved against core.Role and a row CREATED when nothing
 * matched, so relabelling a line of the training matrix could add a job title
 * to the directory every other app in the estate reads.
 */
function RolePickerDialog({
  title,
  jobRoles,
  initial = null,
  linked = false,
  onSave,
  onClose,
}: {
  title: string;
  jobRoles: Role[];
  initial?: number | null;
  linked?: boolean;
  onSave: (roleId: number) => Promise<void>;
  onClose: () => void;
}) {
  const [roleId, setRoleId] = useState<number | null>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (roleId !== null) await onSave(roleId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || roleId === null}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field
        label="Job role"
        hint={
          linked
            ? 'This list belongs to a People Template. Moving it here moves the template’s role with it — they are one thing. Roles are added on the Employees page.'
            : 'From the directory. Add a missing one on the Employees page.'
        }
      >
        <RolePicker roles={jobRoles} value={roleId} onChange={setRoleId} placeholder="Choose a role…" />
      </Field>
    </Modal>
  );
}

/* =========================================================================
   One role's training
   ========================================================================= */

function RoleTraining({
  role,
  jobRoles,
  onBack,
  onChanged,
  onRename,
  renaming,
  onCloseRename,
}: {
  role: TrainingRole;
  jobRoles: Role[];
  onBack: () => void;
  onChanged: () => void;
  onRename: () => void;
  renaming: TrainingRole | null;
  onCloseRename: () => void;
}) {
  const items = useResource<TrainingItem[]>(`/people/training/items?roleId=${role.TrainingRoleId}`);
  const [editing, setEditing] = useState<TrainingItem | null>(null);
  const [adding, setAdding] = useState<'required' | 'optional' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    items.reload();
    onChanged();
  };

  const list = items.data ?? [];
  const required = list.filter((i) => i.Category === 'required');
  const optional = list.filter((i) => i.Category === 'optional');

  return (
    <>
      <div className="row-between wrap mb-4">
        <div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
            ← All roles
          </button>
          <h2 className="mt-2">{role.Name ?? 'No role assigned'}</h2>
          <p className="xs subtle">
            {role.TemplateId
              ? 'Mirrored from a People Template'
              : 'Added on the training matrix'}
          </p>
        </div>
        <div className="row wrap">
          <button type="button" className="btn btn-ghost" onClick={onRename}>
            Change role
          </button>
          {/* A role that mirrors a template cannot be deleted here — the API
              refuses it too. Deleting the template is the honest way to do it,
              because that is the row this one exists because of. */}
          {role.TemplateId === null && (
            <DeleteButton
              what={`the "${role.Name ?? 'unassigned'}" role and its ${role.ItemCount} training ${
                role.ItemCount === 1 ? 'item' : 'items'
              }`}
              label="Delete role"
              onConfirm={() => {
                void api
                  .del(`/people/training/roles/${role.TrainingRoleId}`)
                  .then(() => {
                    onChanged();
                    onBack();
                  })
                  .catch((err: unknown) =>
                    setError(err instanceof Error ? err.message : 'Could not delete the role'),
                  );
              }}
            />
          )}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {items.error && <ErrorBanner message={items.error} />}
      {items.loading && <Loading label="Loading training" />}

      <div className="stack">
        <TrainingList
          title="Required training"
          hint="Everyone in this role needs to complete these."
          items={required}
          onAdd={() => setAdding('required')}
          onEdit={setEditing}
          onDelete={(item) => {
            void api.del(`/people/training/items/${item.TrainingItemId}`).then(reload);
          }}
        />
        <TrainingList
          title="Optional training"
          hint="Available to anyone in this role who wants to grow these skills."
          items={optional}
          onAdd={() => setAdding('optional')}
          onEdit={setEditing}
          onDelete={(item) => {
            void api.del(`/people/training/items/${item.TrainingItemId}`).then(reload);
          }}
        />
      </div>

      {(adding !== null || editing !== null) && (
        <TrainingItemDialog
          item={editing}
          defaultCategory={adding ?? 'required'}
          roleName={role.Name ?? 'this role'}
          onClose={() => {
            setAdding(null);
            setEditing(null);
          }}
          onSave={async (payload) => {
            if (editing) await api.put(`/people/training/items/${editing.TrainingItemId}`, payload);
            else await api.post('/people/training/items', { ...payload, trainingRoleId: role.TrainingRoleId });
            setAdding(null);
            setEditing(null);
            reload();
          }}
        />
      )}

      {renaming && (
        <RolePickerDialog
          title="Change role"
          jobRoles={jobRoles}
          initial={renaming.RoleId}
          linked={renaming.TemplateId !== null}
          onClose={onCloseRename}
          onSave={async (roleId) => {
            await api.put(`/people/training/roles/${renaming.TrainingRoleId}`, { roleId });
            onCloseRename();
            onChanged();
          }}
        />
      )}
    </>
  );
}

function TrainingList({
  title,
  hint,
  items,
  onAdd,
  onEdit,
  onDelete,
}: {
  title: string;
  hint: string;
  items: TrainingItem[];
  onAdd: () => void;
  onEdit: (item: TrainingItem) => void;
  onDelete: (item: TrainingItem) => void;
}) {
  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <h3>{title}</h3>
          <div className="sub">{hint}</div>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onAdd}>
          + Add
        </button>
      </div>
      <div className="card-body">
        {items.length === 0 ? (
          <p className="muted small">Nothing here yet.</p>
        ) : (
          items.map((item) => (
            <div key={item.TrainingItemId} className="mini-row row-between wrap">
              <div className="grow">
                <span className="strong">{item.Title}</span>
                {item.Link && (
                  <>
                    {' '}
                    <a className="xs" href={item.Link} target="_blank" rel="noopener noreferrer">
                      open ↗
                    </a>
                  </>
                )}
              </div>
              <div className="row">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onEdit(item)}>
                  Edit
                </button>
                <DeleteButton what={`"${item.Title}"`} onConfirm={() => onDelete(item)} />
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function TrainingItemDialog({
  item,
  defaultCategory,
  roleName,
  onSave,
  onClose,
}: {
  item: TrainingItem | null;
  defaultCategory: 'required' | 'optional';
  roleName: string;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(item?.Title ?? '');
  const [link, setLink] = useState(item?.Link ?? '');
  const [category, setCategory] = useState<'required' | 'optional'>(item?.Category ?? defaultCategory);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ title: title.trim(), link: link.trim() || null, category });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={item ? 'Edit training' : `Add training — ${roleName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="Training name">
        <input
          className="input"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Security awareness, HIPAA compliance…"
        />
      </Field>
      <Field label="Link" hint="Where the training lives. Optional.">
        <input
          className="input"
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://…"
        />
      </Field>
      <Field label="Category">
        <select
          className="select"
          value={category}
          onChange={(e) => setCategory(e.target.value as 'required' | 'optional')}
        >
          <option value="required">Required</option>
          <option value="optional">Optional</option>
        </select>
      </Field>
    </Modal>
  );
}
